import React, { useEffect, useRef, useState } from 'react';

import { Autocomplete, AutocompleteItem, AutocompleteProps } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import useAutoCompletePackageCategory from 'hooks/packageCategory/useAutoCompletePackageCategory';

import SelectorButton from './SelectorButton';

export interface PackageCategoryAutoCompleteValue extends AutocompleteItem {
  packageCategoryId: string;
  label: string;
}

interface IPackageCategoryAutoCompleteProps
  extends Omit<
    AutocompleteProps,
    'data' | 'value' | 'onChange' | 'filter' | 'onItemSubmit' | 'rightSection'
  > {
  value: PackageCategoryAutoCompleteValue | null;
  onChange: (item: PackageCategoryAutoCompleteValue | null) => void;
}

const PackageCategoryAutoComplete = ({
  value,
  onChange,
  ...autocompleteProps
}: IPackageCategoryAutoCompleteProps): JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [options, setOptions] = useState<AutocompleteItem[]>([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue<string>(search, 250);

  const { data, isSuccess, isLoading } = useAutoCompletePackageCategory({
    search: debouncedSearch,
  });

  const handleSearchChange = (query: string): void => {
    setSearch(query);
    onChange(null);
  };
  const handleItemSubmit = (item: PackageCategoryAutoCompleteValue): void => {
    setSearch('');
    onChange(item);
  };
  const handleClearSearch = (): void => {
    setSearch('');
  };

  const RightSection = (
    <SelectorButton
      isLoading={isLoading}
      hasText={!!(value?.label || search)}
      handleClick={() => handleSearchChange('')}
      handleFocus={() => inputRef?.current?.focus()}
    />
  );

  useEffect(() => {
    if (isSuccess && data) {
      setOptions(
        data.data.results.map(packageCategory => ({
          value: packageCategory.packageCategoryId,
          label: packageCategory.name,
          packageCategoryId: packageCategory.packageCategoryId,
        })),
      );
    }
  }, [isSuccess, data]);

  return (
    <Autocomplete
      ref={inputRef}
      rightSection={RightSection}
      value={value?.label || search}
      onChange={handleSearchChange}
      onItemSubmit={handleItemSubmit}
      onDropdownClose={handleClearSearch}
      filter={() => true}
      data={options}
      withinPortal={true}
      {...autocompleteProps}
      placeholder='Select Package Category'
    />
  );
};

export default PackageCategoryAutoComplete;
